/* eslint-disable jsx-a11y/anchor-is-valid */
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useNavigate, useParams } from "react-router";
import EmployeeCard from "../../components/EmployeeCard";
import GiscoAutocomplete from "../../components/GiscoAutocomplete";
import { CountryAutoComplete } from "../../models/Autocomplete";
import { AxiosErrorDetail } from "../../models/AxiosErrorDetail";
import { CalculationPreview, Claim, PostClaim } from "../../models/Claim";
import { Event } from "../../models/Event";
import Feature from "../../models/GISCO/Feature";
import { PageableCollection } from "../../models/PageableCollection";
import restClient from "../../services/clients/AnonymousRestClient";
import GiscoFeatureDialog from "../../components/GiscoFeatureDialog";
import { Institution } from "../../models/Institution";
import { GiscoFeatureState } from "../../models/GISCO/GiscoFeatureState";
import { Country } from "../../models/Country";
import { SkeletonClaimLoad } from "../../components/SkeletonClaimLoad";

const CreateTravelClaim = () => {

    const { eventNumber } = useParams();

    // #region Init States

    let claimInitState: PostClaim = {
        eventId     : -1,
        firstname   : "",
        lastname    : "",
        email       : "",
        phone       : "",
        institution : "",
        street      : "",
        postalCode  : "",
        city        : "",
        countryId   : -1,
        latitude    : -1,
        longitude   : -1,
        travelType  :  0,
        nightsSpend :  0
    };

    let eventInitState: Event = {
        id         : -1,
        number     : "?",
        title      : "?",
        startDate  : new Date(),
        endDate    : new Date(),
        offlineDate: new Date(),
        location   : "?",
        maxNights  : 2,
        accommodationEligible: true,
        distanceBandVersion: 1,
        distanceBandVersionName: "",
        distanceBandVersionDescription: "",
        languages: [
            {
                language: "de",
                title   : "?"
            }
        ],
        contacts: [
            {
                contactId   : 1,
                firstName   : "Max",
                lastName    : "Mustermann",
                displayName : "Max Mustermann",
                jobTitle : "IT-Administrator",
                email    : "mmustermann@era.int",
                phone    : "+(49) 065193737-740"
            }
        ]
    }

    // #endregion

    // #region States & Hooks

    const [seminar, setSeminar]         = useState<Event>(eventInitState);;
    const [claim, setClaim]             = useState<PostClaim>(claimInitState);
    const [countries, setCountries]     = useState<CountryAutoComplete[]>([])
    const [calcPreview, setCalcPreview] = useState<CalculationPreview | null>(null);
    const [detailPane, setDetailPane]   = useState<Boolean>(false);
    
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting]   = useState<boolean>(false);
    const [isLoading, setIsLoading]         = useState<boolean>(true);
    const [error, setError]                 = useState<string>("");

    const [giscoFeature, setGiscoFeature] = useState<GiscoFeatureState>({
        show:    false,
        feature: undefined
    })

    const navigate = useNavigate();

    // #endregion

    // #region Functions

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint for calculate
     * refund rate.
     * @param e Form submit event that needs to be handled.
     * @returns Calculated unit cost preview.
     */
    async function handleCalcSubmit(e: React.FormEvent<HTMLFormElement>): Promise<CalculationPreview | null>
    {
        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();

        setIsCalculating(true);
        let calcResponse: CalculationPreview | null = null;

        await restClient.post<CalculationPreview>("/api/travelclaim/calculate", {
            eventId:     claim.eventId,
            latitude:    claim.latitude,
            longitude:   claim.longitude,
            traveltype:  claim.travelType,
            countryid:   claim.countryId,
            nightsspend: claim.nightsSpend,
            countryId:   claim.countryId,
            institution: claim.institution
        })
            .then(response => {
                if (response.status === 200) {
                    setCalcPreview(response.data);
                    setError("");
                    calcResponse = response.data;
                }
            })
            .catch((err: AxiosError) => {
                if (err.response?.status === 400)
                {
                    /* TODO: Add more specific error message */
                    setError("Please fill out all required fields.")
                }
                if (err.response?.data && (err.response.data as AxiosErrorDetail).errorCode === 0)
                {
                    setError((err.response.data as AxiosErrorDetail).errorMessage)
                }
            })
            .finally(() => { setIsCalculating(false); })

        return calcResponse; // default value NULL;
    }

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint to create a new 
     * unit cost claim form. 
     * @param e Form submit event that needs to be handled.
     * @returns Newly created claim.
     */
    async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<Claim | null> {

        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();

        setIsSubmitting(true);
        let newClaim: Claim | null = null;

        await restClient.post<Claim>("/api/travelclaim/", claim)
            .then(response => {
                if (response.status === 200)
                {
                    newClaim = response.data;
                    navigate("/claims/completed");
                }
            })
            .catch((err: AxiosError) =>
            {
                if (err.response?.data && (err.response.data as AxiosErrorDetail))
                {
                    setError((err.response?.data as AxiosErrorDetail).errorMessage);
                }
            })
            .finally(() => { setIsSubmitting(false); })

        return newClaim;
    }

    /**
     * Method that handles event that will be triggered when autocomplete input value has changed. 
     * @param feature Feature that has been selected by the site user. 
     */
    async function onAutocompleteChange(feature: Feature): Promise<void> {

        /* Check if selected feature has all mandatory field filled */
        if (feature.properties.type === "house" && feature.properties.name && feature.properties.street &&
            feature.properties.postcode         && feature.properties.city && feature.properties.countrycode) {

            setGiscoFeature({
                show   : false,
                feature: feature
            });

            await onGiscoDialogSuccess({
                name: feature.properties.name,
                address: {
                    street:     feature.properties.street,
                    postalCode: feature.properties.postcode,
                    city:       feature.properties.city,
                    country:    feature.properties.countrycode
                }
            }, feature);
        }
        else {

            /* Proposal is missing required fields */
            setGiscoFeature({
                show   : true,
                feature: feature
            })
        }
    }

    /**
     * Callback method that gets executed by child component GiscoFeatureDialog when child component closed successful.
     * @param institution Place of employement with all required data.
     */
    async function onGiscoDialogSuccess(institution: Institution, feature: Feature | null): Promise<void> {

        let countryId: number = -1;

        await restClient.get<PageableCollection<Country>>("/api/countries?iso_3166=" + institution.address.country)
            .then(response => {
                if (response.status === 200 && response.data.count === 1)
                {
                    countryId = response.data.results[0].id;
                }
            });

        if (countryId === -1) { setError("The specified country is invalid.") }

        setClaim(x => ({
            ...x, // Spread operator to copy data from current state
            // Refresh address data
            institution: institution.name,
            street     : institution.address.street,
            postalCode : institution.address.postalCode,
            city       : institution.address.city,
            countryId  : countryId,
            latitude   : feature?.geometry.coordinates[1] ?? 0,
            longitude  : feature?.geometry.coordinates[0] ?? 0
        }));

        setGiscoFeature(x => ({ ...x, show: false }));
    }

    /**
     * Callback metho that gets executed by child component GiscoFeatureDialog when child component closed unsuccessful.
     */
    function onGiscoDialogAbort(): void
    {
        setGiscoFeature({
            show: false,
            feature: undefined
        })
    }

    // #endregion

    // #region Render Functions

    /**
     * Function renders claim form for entering unit costs informations.
     * @returns JSX Element 
     */
    const renderClaimForm = (): React.JSX.Element => {
        return (
            <div className="card">
                <div className="card-header">
                    Travel | Accommodation Calculator
                </div>
                <div className="card-body">
                    <form onSubmit={handleCalcSubmit}>

                        <div className="row">

                            {seminar.accommodationEligible && (
                                <div className="form-group col-12 text-start">
                                    <label htmlFor="NightsSpend" className="fw-bold">Nights spent</label>
                                    <select className="form-select" id="NightsSpend" value={claim.nightsSpend} onChange={e => setClaim(x => ({ ...x, nightsSpend: Number(e.target.value) }))}>
                                        {
                                            Array.from(Array(seminar.maxNights ? seminar.maxNights + 1 : getMaxNights(seminar.startDate, seminar.endDate)).keys()).map((entry, index) =>
                                            {
                                                return (
                                                    <option key={index} value={entry}>
                                                        {entry}
                                                    </option>
                                                )
                                            })
                                        }
                                    </select>
                                </div>
                            )}

                            <div className="form-group col-12 text-start">
                                <label htmlFor="TravelType" className="fw-bold">Travel Type</label>
                                <select id="TravelType" defaultValue={claim.travelType} name="travelType" className="form-select" onChange={e => setClaim(x => ({ ...x, travelType: Number(e.target.value) }))} value={claim.travelType} >
                                    { /* Static options list for travel type */}
                                    <option value="0" key={0}>Plane or combined</option>
                                    <option value="1" key={1}>Land Travel</option>
                                </select>
                            </div>

                            <hr />

                            { /* AutoComplete Form for select place via GISCO API */ }
                            <div className="form-group col-12 text-start">
                                <Tooltip id="giscoTTip" />
                                <label htmlFor="sourceLocation" className="fw-bold">Place of employment</label><span><i className="bi bi-question-circle-fill ms-2" data-tooltip-id="giscoTTip" data-tooltip-content="Please enter the address of your employer in the following order: Organisation, street and city" data-tooltip-place="top"></i></span>
                                <GiscoAutocomplete id="sourceLocation" onFeatureChange={onAutocompleteChange} />
                            </div>

                            <GiscoFeatureDialog show={giscoFeature.show} feature={giscoFeature.feature ?? null} onConfirm={onGiscoDialogSuccess} onAbort={onGiscoDialogAbort} />

                            <div className="form-group col-12 text-start">
                                <label htmlFor="Institution" className="fw-bold">Institution / Company</label>
                                <input id="Institution" name="institution" className="form-control" disabled={true} value={claim.institution} />
                            </div>
                            <div className="form-group col-12 text-start">
                                <label htmlFor="Street" className="fw-bold">Street</label>
                                <input id="Street" name="street" className="form-control" disabled={true} value={claim.street} />
                            </div>
                            <div className="form-group col-12 col-md-4 text-start">
                                <label htmlFor="PostalCode" className="fw-bold">Postal Code</label>
                                <input id="PostalCode" name="postalCode" className="form-control" disabled={true} value={claim.postalCode} />
                            </div>
                            <div className="form-group col-12 col-md-4 text-start">
                                <label htmlFor="City" className="fw-bold">City</label>
                                <input id="City" name="city" className="form-control" disabled={true} value={claim.city} />
                            </div>
                            <div className="form-group col-12 col-md-4 text-start">
                                <label htmlFor="Country" className="fw-bold">Country</label>
                                <select id="Country" name="country" className="form-control" disabled={true} onChange={e => setClaim(x => ({ ...x, countryId: Number(e.target.value) }))} value={claim.countryId}>
                                    {
                                        countries.map((entry, index) => {
                                            return (
                                                <option key={"country#" + index} value={entry.value}>
                                                    {entry.displayValue}
                                                </option>
                                            )
                                        })
                                    }
                                </select>
                            </div>

                            <hr />

                        </div>

                        <div className="form-group">
                            {isCalculating
                                ? <button type="button" className="btn btn-outline-primary w-100" disabled={true}><img src="/Loading.gif" style={{ width: "32px" }} alt="loading" /></button>
                                : <button className="btn btn-primary w-100" type="submit">Calculate contribution</button>
                            }
                        </div>

                    </form>
                </div>
            </div>
        );
    }

    /**
     * Function renders preview card for calulated values.
     * @returns JSX Element
     */
    const renderPreview = (): React.JSX.Element => {
        return (
            <div className="card">
                <div className="card-header">
                    Expected Refund
                </div>
                <div className="card-body text-start">

                    <div className="row">
                        <h4 className="mb-2 fw-bold">Total amount of transfer: &euro; {calcPreview ? (calcPreview.tAmount + (calcPreview.aAmountTotal ? calcPreview.aAmountTotal : 0)).toFixed(2) : 0}</h4>
                        <a href="#" onClick={() => setDetailPane(!detailPane)} style={{ textDecoration: "none" }}>
                            {detailPane ? "Hide Details" : "Show Details"}
                            <i className={detailPane ? "ms-1 bi bi-caret-up-fill" : "ms-1 bi bi-caret-down-fill"}></i>
                        </a>
                    </div>

                    <section className={detailPane ? "bg-light p-2 border" : "d-none"}>

                        <hr />

                        <div className="row">
                            <div className="form-group">
                                <a href="#" onClick={e => setCalcPreview(null)}><i className="bi bi-pencil me-2"></i>Edit transport and accommodation data</a>
                            </div>
                        </div>

                        <dl className="row">
                            <dt className="col-12">
                                <label><i className="bi bi-arrow-right me-2"></i>Your place of employment</label>
                            </dt>
                            <dd className="col-12">
                                {claim.institution + " - " + claim.street + " " + claim.postalCode + " " + claim.city}
                            </dd>
                        </dl>
                        <dl className="row">
                            <dt className="col-12">
                                <label><i className="bi bi-arrow-left me-2"></i>Event Location</label>
                            </dt>
                            <dd className="col-12">
                                {seminar.location}
                            </dd>
                        </dl>
                        <dl className="row">
                            <dt className="col-12">
                                <label><i className="bi bi-map me-2"></i>Distance</label>
                            </dt>
                            <dd className="col-12">
                                {calcPreview ? Math.round(calcPreview.distance) : 0} km
                            </dd>
                        </dl>
                        <dl className="row">
                            <dt className="col-12">
                                <label><i className={claim.travelType === 0 ? "bi bi-airplane me-2" : "bi bi-train-front me-2"}></i>Transportation Type</label>
                            </dt>
                            <dd className="col-12">
                                {calcPreview?.travelType === 0 ? "Plane or Combined" : "Land Travel"}
                            </dd>
                        </dl>
                        <dl className="row">
                            <dt className="col-12">
                                <label>Travel Refund</label>
                            </dt>
                            <dd className="col-12">
                                &euro; {calcPreview?.tAmount}
                            </dd>
                        </dl>

                        <hr />
                        {seminar.accommodationEligible &&
                            <>
                                <dl className="row">
                                    <dt className="col-12">
                                        <label><i className="bi bi-house me-2"></i>Nights Spent</label>
                                    </dt>
                                    <dd className="col-12">
                                        {calcPreview?.nights}
                                    </dd>
                                </dl>
                                <dl className="row">
                                    <dt className="col-12">
                                        <label>Amount per night</label>
                                    </dt>
                                    <dd className="col-12">
                                        &euro; {calcPreview?.aAmount}
                                    </dd>
                                </dl>
                                <dl className="row">
                                    <dt className="col-12">
                                        <label>Total Amount</label>
                                    </dt>
                                    <dd className="col-12">
                                        &euro; {calcPreview ? calcPreview.aAmountTotal ? calcPreview.aAmountTotal : 0 : 0}
                                    </dd>
                                </dl>
                            </>
                        }

                        <div className="row">
                            <div className="form-group">
                                <a href="/#" onClick={e => setCalcPreview(null)}><i className="bi bi-pencil me-2"></i>Edit transport and accommodation data</a>
                            </div>
                        </div>

                    </section>

                    <hr />

                    <form onSubmit={handleSubmit}>

                        <div className="row">
                            <div className="form-group col-12 col-md-6 text-start mb-1">
                                <label htmlFor="Firstname" className="fw-bold">Participant Firstname</label>
                                <input id="Firstname" name="firstname" className="form-control" onChange={e => setClaim(x => ({ ...x, firstname: e.target.value }))} value={claim.firstname} />
                            </div>
                            <div className="form-group col-12 col-md-6 text-start mb-1">
                                <label htmlFor="Lastname" className="fw-bold">Participant Lastname</label>
                                <input id="Lastname" name="lastname" className="form-control" onChange={e => setClaim(x => ({ ...x, lastname: e.target.value }))} value={claim.lastname} />
                            </div>
                            <div className="form-group col-12 text-start">
                                <label htmlFor="Phone" className="fw-bold"><i className="bi bi-phone me-1"></i>Phone</label>
                                <input id="Phone" name="phone" className="form-control" onChange={e => claim.phone = e.target.value} />
                            </div>
                            <div className="form-group col-12 text-start">
                                <label htmlFor="Email" className="fw-bold"><i className="bi bi-envelope-at me-1"></i>Email</label>
                                <input id="Email" name="email" className="form-control" onChange={e => claim.email = e.target.value} />
                            </div>
                        </div>

                        <hr /> 

                        <div className="form-group col-12">
                            {isSubmitting 
                                ? <button type="button" className="btn btn-outline-primary w-100" disabled={true}><img src="/Loading.gif" style={{ width: "32px" }} alt="imgLoading" /></button>
                                : <button className="btn btn-primary w-100" type="submit">Save Claim</button>
                            }
                            
                        </div>

                    </form>

                </div>
            </div>
        );
    }

    /**
     * Returns the maximum amount of nights that can be spend for the current event. 
     * @returns number with maximal nights.
     */
    const getMaxNights = (date1: Date, date2: Date): number => {

        let difference = new Date(date2).getTime() - new Date(date1).getTime();
        let diffrenceDays = difference / (1000 * 3600 * 24);

        // Event Days (+1) and grace day (+1)
        return diffrenceDays > 0 ? diffrenceDays + 1 : 0;
    }

    // #endregion

    useEffect(() => {

        setIsLoading(true);

        /* Fetch event details for the specified eventId from API */
        Promise.all([
            restClient.get<PageableCollection<Event>>("/api/events?number=" + eventNumber),
            restClient.get<CountryAutoComplete[]>("/api/countries/autocomplete")
        ])
            .then(([eventResponse, countriesResponse]) => {

                /* Check response for all promises */
                if (eventResponse.status === 200 && eventResponse.data.results.length === 1) { setSeminar(eventResponse.data.results[0]); claim.eventId = eventResponse.data.results[0].id; }
                if (countriesResponse.status === 200) { setCountries(countriesResponse.data); }
            })
            .catch(error => {
                /* TODO: Propert error handling */
            })
            .finally(() => {
                /* Disable Loading aninmation and show form */
                setIsLoading(false);
            })
    }, []);

    return (<>
        {
            isLoading ?
                <SkeletonClaimLoad />
                :
                <main className="container text-center mb-5">

                    <div className="row">
                        <div className="col-12">
                            <img src="/brand.png" alt="brand" className="brand" />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 mb-2">
                            <h2>Unit Costs Claim Form</h2>
                        </div>
                    </div>

                    {(error !== "") && (
                        <div className="row">
                            <div className="col-12">
                                <div className="alert alert-danger fw-bold">
                                    {error}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row">

                        <div className="col-12 col-md-8 order-2 order-md-1">

                            {calcPreview ? renderPreview() : renderClaimForm()}

                        </div>

                        <div className="col-12 col-md-4 mb-2 order-1 order-md-2">

                            <div className="card">
                                <div className="card-header">
                                    Event Details
                                </div>
                                <div className="card-body text-start">

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label>Event Title</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {seminar.title}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label>Event Number</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {seminar.number}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-calendar me-1"></i>Seminar Start Date</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {new Date(seminar.startDate).toDateString()}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-calendar me-1"></i>Seminar End Date</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {new Date(seminar.endDate).toDateString()}
                                        </dd>
                                    </dl>

                                    <dl className="row">
                                        <dt className="col-6 col-md-12">
                                            <label><i className="bi bi-geo me-1"></i>Event Location</label>
                                        </dt>
                                        <dd className="col-6 col-md-12">
                                            {seminar.location}
                                        </dd>
                                    </dl>

                                </div>
                            </div>

                            {(seminar.contacts && seminar.contacts.length > 0) && (
                                <div className="card mt-2">
                                    <div className="card-header">
                                        Contact
                                    </div>
                                    <div className="card-body">
                                        {
                                            seminar.contacts.map((value, index) => {
                                                return (
                                                    <EmployeeCard key={index} employee={value} seminarnumber={seminar.number} />
                                                )
                                            })
                                        }
                                    </div>
                                </div>
                            )}

                        </div>

                    </div>

                </main>
        }
    </>);
}

export default CreateTravelClaim;